<script setup lang="ts">
import type { PropType } from "vue";
import { onMounted, ref, computed, onUpdated } from "vue";
import { useSearchStore } from "@/stores/search";
import type { Category } from "@/types/category";
import type { Sorting } from "@/types/sorting";
import InputComponent from "@/components/InputComponent/InputComponent.ce.vue";
import TextInput from "@/components/InputComponent/TextInput.ce.vue";
import RangeInput from "@/components/RangeInput/RangeInput.ce.vue";
import {
    submitTypePrice,
    submitTypeTravelNumber,
} from "@/globalConsts/submitTypes";
import { filterNameTranslation } from "@/globalConsts/filterNameTranslation";
import type { FilterItem } from "@/types/filterItem";
import type { FilterCategory } from "@/types/filterCategory";
import type { InputEventProps } from "@/types/inputEventProps";
import {
    getEventByContentType,
    getTripIdFunnelEvent,
    pushEventDataLayer,
} from "@/services/funnelEvent";
import type PriceFunnelEvent from "@/types/funnel/priceFunnelEvent";
import { SELECT_CONTENT } from "@/globalConsts/searchFunnelEvents";
import { t } from "@/services/i18n";

const props = defineProps({
    filter: {
        type: Object as PropType<Category> | null,
        default: null,
        required: false,
    },
    sortingFilter: {
        type: Array as PropType<Sorting[]>,
        default: () => [],
        required: false,
    },
});
const store = useSearchStore();
const {
    saveSubmittedFilter,
    saveSubmittedPriceRatio,
    saveSubmittedTravelNumber,
    resetSeaContentAndTeaser,
} = store;
const filterModalContent = ref<HTMLDivElement>();
const isActive = ref<HTMLDivElement | null | undefined>();
const showModal = ref(false);
const priceFilter = ref(store.getCurrentPriceRange);
const priceRange = computed(() => {
    return store.getPriceRange;
});
const currentTravelNumber = computed(() => {
    return store.getTravelNumber;
});
const filterItem: FilterCategory = filterNameTranslation[props.filter.uid];

const inputValue = (item: FilterItem): string => {
    let value: string;

    if (item.uid === "travelNumber") {
        value = store.submitted.travelNumber || "";
    } else {
        value = item.uid;
    }

    return value;
};

function isCategoryTreeActive() {
    isActive.value = filterModalContent.value?.querySelector(".is-active");
}

async function updateFilter(inputEventProps: InputEventProps): Promise<void> {
    await saveSubmittedFilter(props.filter.submitType, inputEventProps.value);
    resetSeaContentAndTeaser();

    pushEventDataLayer(
        getEventByContentType(inputEventProps, filterItem.contentType)
    );
    isCategoryTreeActive();
}

async function updatePriceFilter(value: number[]): Promise<void> {
    await saveSubmittedPriceRatio(value[0], value[1]);
    pushEventDataLayer({
        event: SELECT_CONTENT,
        content_type: filterItem.contentType,
        currency: store.configuration.currency,
        max_price: value[1],
        min_price: value[0],
    } as PriceFunnelEvent);
    isCategoryTreeActive();
}

async function updateTravelNumber(value: string): Promise<void> {
    await saveSubmittedTravelNumber(value);
    resetSeaContentAndTeaser();
    isCategoryTreeActive();
}
function pushTravelNumberEvent(): void {
    pushEventDataLayer(getTripIdFunnelEvent(currentTravelNumber.value));
}

function clearModel() {
    showModal.value = !showModal.value;
}

function isCategoryChecked(id: string) {
    if (store.submitted.tags) {
        return store.submitted.tags.includes(id);
    }
}

onMounted(() => {
    isCategoryTreeActive();
});
onUpdated(() => {
    isCategoryTreeActive();
});
</script>
<template>
    <button
        v-if="filter && filter.uid !== 'organizer'"
        @click="showModal = !showModal"
        class="[ c-filter-bar__button ]"
        :class="{ 'is-active': isActive }"
        @keydown.esc="clearModel"
        :aria-label="t('open_filter') + ' ' + t(filterItem.title)"
    >
        {{ t(filterItem.title) }}
    </button>
    <transition name="modal">
        <div v-show="showModal" class="[ c-modal__mask ]">
            <div class="[ c-modal__close ]" @click="clearModel"></div>
            <div
                ref="filterModalContent"
                class="[ c-modal__content ]"
                v-if="filter && sortingFilter.length === 0"
            >
                <div class="[ c-modal__header ]">
                    <div class="[ c-modal__headline ]">
                        <span>{{ t(filterItem.title) }}</span>
                    </div>
                    <button @click="clearModel" class="[ c-modal__btn-close ]">
                        <i class="[ c-icon ] [ icon icon-close ]"></i>
                    </button>
                </div>
                <div class="[ c-modal__body ]">
                    <div
                        v-if="filter.submitType === submitTypePrice"
                        class="[ c-modal__input ]"
                    >
                        <range-input
                            @changeRangeInput="
                                (value: number[]) => updatePriceFilter(value)
                            "
                            :range="priceRange"
                            :current-value="priceFilter"
                            :prefix="t('currency_sign')"
                            :range-label-min="t('min_price')"
                            :range-label-max="t('max_price')"
                        />
                    </div>
                    <div
                        v-if="filter.submitType === submitTypeTravelNumber"
                        class="[ c-modal__input ]"
                    >
                        <text-input
                            :value="currentTravelNumber"
                            @on-text-input="
                                (value: string) => updateTravelNumber(value)
                            "
                            @on-leave-input="pushTravelNumberEvent()"
                            :placeholder="filterItem.title"
                        />
                    </div>
                    <div
                        v-else
                        v-for="item in filter.categories"
                        :key="item.uid"
                        class="[ c-modal__input ]"
                    >
                        <input-component
                            :type="item.type"
                            :value="inputValue(item)"
                            :label="item.title"
                            :name="filter.uid"
                            :checked="isCategoryChecked(item.uid)"
                            @on-input="
                                (inputEventProps: InputEventProps) =>
                                    updateFilter(inputEventProps)
                            "
                        />
                    </div>
                </div>
                <div class="[ c-modal__footer ]">
                    <button
                        class="[ c-modal__footer-btn ]"
                        @click="showModal = false"
                    >
                        {{ t("filter") }}
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>
